import { useCallback, useEffect, useState } from 'react';
import { DoorOpen, Edit, Loader2, Plus, Power, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import LoeschenMitGrundDialog from '../ui/loeschen-mit-grund-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { usePermission } from '../rbac/use-permission';
import { P } from '../../lib/rbac/permissions.generated';
import {
  deleteRoom,
  getRooms,
  getRoomTags,
  updateRoom,
  uploadRoomImage,
  type Room,
  type RoomTag,
  type RoomUpdateData,
} from '../../lib/rooms';
import RoomCreateWizardModal from './RoomCreateWizardModal';

/**
 * Räume anlegen, bearbeiten, (de)aktivieren und löschen — unter „Administration ›
 * Räume & Tags“ (T-0333, Entscheidung 04.11.2026). Bis dahin lag das im Raumplan;
 * der zeigt jetzt nur noch Belegung und verweist hierher.
 */

type Lebenslauf = 'draft' | 'active' | 'inactive';

const lebenslauf = (raum: Room): Lebenslauf => raum.status || (raum.aktiv ? 'active' : 'inactive');

const STATUS_TEXT: Record<Lebenslauf, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  inactive: 'Inaktiv',
};

const LEERES_FORMULAR = {
  name: '',
  standort_id: 0,
  kapazitaet: 1,
  aktiv: true,
  address: '',
  address_street: '',
  address_house_number: '',
  address_postal_code: '',
  address_city: '',
  directions: '',
  image_url: '',
  size_m2: '' as number | string,
  cost_cents: '' as number | string | null,
  cost_interval: '' as string,
  tag_ids: [] as number[],
};

export default function RaumVerwaltung({ standortId }: Readonly<{ standortId: number | null }>) {
  const darfAnlegen = usePermission(P.raumplan.raeume_anlegen);
  const darfBearbeiten = usePermission(P.raumplan.raeume_bearbeiten);
  const darfUmschalten = usePermission(P.raumplan.raeume_de_aktivieren);
  const darfLoeschen = usePermission(P.raumplan.raeume_loeschen);

  const [raeume, setRaeume] = useState<Room[]>([]);
  const [tags, setTags] = useState<RoomTag[]>([]);
  const [laedt, setLaedt] = useState(false);
  const [assistentOffen, setAssistentOffen] = useState(false);
  const [fortsetzen, setFortsetzen] = useState<number | undefined>(undefined);
  const [bearbeitet, setBearbeitet] = useState<Room | null>(null);
  const [formular, setFormular] = useState(LEERES_FORMULAR);
  const [zuLoeschen, setZuLoeschen] = useState<Room | null>(null);

  const laden = useCallback(async () => {
    if (!standortId) return;
    setLaedt(true);
    try {
      setRaeume(await getRooms(standortId));
    } catch {
      toast.error('Räume konnten nicht geladen werden');
    } finally {
      setLaedt(false);
    }
  }, [standortId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  useEffect(() => {
    getRoomTags()
      .then(setTags)
      .catch(() => setTags([]));
  }, []);

  const bearbeiten = (raum: Room) => {
    setBearbeitet(raum);
    setFormular({
      name: raum.name,
      standort_id: raum.standort_id,
      kapazitaet: raum.kapazitaet || 1,
      aktiv: raum.aktiv,
      address: raum.address || '',
      address_street: raum.address_street || '',
      address_house_number: raum.address_house_number || '',
      address_postal_code: raum.address_postal_code || '',
      address_city: raum.address_city || '',
      directions: raum.directions || '',
      image_url: raum.image_url || '',
      size_m2: raum.size_m2 ?? '',
      cost_cents: raum.cost_cents ?? null,
      cost_interval: raum.cost_interval || '',
      tag_ids: raum.tag_ids || [],
    });
  };

  const speichern = async () => {
    if (!bearbeitet) return;
    try {
      const payload = {
        ...formular,
        size_m2: typeof formular.size_m2 === 'string' ? (formular.size_m2 === '' ? null : parseInt(formular.size_m2)) : formular.size_m2,
        cost_cents: typeof formular.cost_cents === 'string' ? (formular.cost_cents === '' ? null : parseInt(formular.cost_cents)) : formular.cost_cents,
        cost_interval: formular.cost_interval === '' ? null : formular.cost_interval,
      };
      await updateRoom(bearbeitet.id, payload as unknown as RoomUpdateData);
      toast.success('Raum gespeichert');
      setBearbeitet(null);
      void laden();
    } catch {
      toast.error('Raum konnte nicht gespeichert werden');
    }
  };

  const umschalten = async (raum: Room) => {
    const neu = lebenslauf(raum) === 'active' ? 'inactive' : 'active';
    try {
      await updateRoom(raum.id, { status: neu } as unknown as RoomUpdateData);
      toast.success(neu === 'active' ? 'Raum aktiviert' : 'Raum deaktiviert');
      void laden();
    } catch (e) {
      toast.error(e instanceof Error && e.message ? e.message : 'Status konnte nicht geändert werden');
    }
  };

  const loeschen = async (grund: string) => {
    if (!zuLoeschen) return;
    await deleteRoom(zuLoeschen.id, grund);
    toast.success('Raum gelöscht');
    setZuLoeschen(null);
    void laden();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center">
            <DoorOpen className="mr-2 h-5 w-5" />
            Räume
          </CardTitle>
          <CardDescription>Räume des gewählten Standorts. Die Belegung steht im Raumplan.</CardDescription>
        </div>
        {darfAnlegen && standortId ? (
          <Button
            onClick={() => {
              setFortsetzen(undefined);
              setAssistentOffen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Raum anlegen
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {!standortId ? (
          <p className="text-sm text-muted-foreground">Bitte oben einen Standort wählen.</p>
        ) : laedt ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-primary" aria-label="Räume werden geladen" />
          </div>
        ) : raeume.length === 0 ? (
          <p className="text-sm text-muted-foreground">An diesem Standort gibt es noch keine Räume.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {raeume.map((raum) => {
              const stand = lebenslauf(raum);
              return (
                <li key={raum.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{raum.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {raum.kapazitaet ? `${raum.kapazitaet} Personen` : 'Kapazität offen'}
                      {raum.size_m2 ? ` · ${raum.size_m2} m²` : ''}
                    </div>
                  </div>
                  <Badge variant={stand === 'active' ? 'default' : 'secondary'}>{STATUS_TEXT[stand]}</Badge>
                  <div className="flex items-center gap-1">
                    {stand === 'draft' && darfAnlegen ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setFortsetzen(raum.id);
                          setAssistentOffen(true);
                        }}
                      >
                        Weiter anlegen
                      </Button>
                    ) : null}
                    {stand !== 'draft' && darfUmschalten ? (
                      <Button size="sm" variant="ghost" onClick={() => void umschalten(raum)}>
                        <Power className="mr-1 h-4 w-4" />
                        {stand === 'active' ? 'Deaktivieren' : 'Aktivieren'}
                      </Button>
                    ) : null}
                    {darfBearbeiten ? (
                      <Button size="icon" variant="ghost" aria-label={`${raum.name} bearbeiten`} onClick={() => bearbeiten(raum)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                    ) : null}
                    {darfLoeschen ? (
                      <Button size="icon" variant="ghost" aria-label={`${raum.name} löschen`} onClick={() => setZuLoeschen(raum)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      {standortId ? (
        <RoomCreateWizardModal
          open={assistentOffen}
          onOpenChange={(offen) => {
            setAssistentOffen(offen);
            if (!offen) setFortsetzen(undefined);
          }}
          standortId={standortId}
          resumeRoomId={fortsetzen}
          onCreated={() => {
            setAssistentOffen(false);
            setFortsetzen(undefined);
            void laden();
          }}
        />
      ) : null}

      <Dialog open={bearbeitet !== null} onOpenChange={(offen) => !offen && setBearbeitet(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Raum bearbeiten</DialogTitle>
            <DialogDescription>Bearbeite die Raumdaten.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="raum-name" className="text-right text-sm font-medium">Name</label>
              <Input id="raum-name" className="col-span-3" placeholder="Raumname" value={formular.name} onChange={(e) => setFormular({ ...formular, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="raum-adresse" className="text-right text-sm font-medium">Adresse</label>
              <Input id="raum-adresse" className="col-span-3" placeholder="Straße, Nr., PLZ Ort" value={formular.address} onChange={(e) => setFormular({ ...formular, address: e.target.value })} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="raum-strasse" className="text-right text-sm font-medium">Straße *</label>
              <Input id="raum-strasse" className="col-span-3" placeholder="Straße" value={formular.address_street} onChange={(e) => setFormular({ ...formular, address_street: e.target.value })} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="raum-hausnummer" className="text-right text-sm font-medium">Hausnummer *</label>
              <Input id="raum-hausnummer" className="col-span-3" placeholder="Hausnummer" value={formular.address_house_number} onChange={(e) => setFormular({ ...formular, address_house_number: e.target.value })} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="raum-plz" className="text-right text-sm font-medium">PLZ *</label>
              <Input id="raum-plz" className="col-span-3" placeholder="PLZ" value={formular.address_postal_code} onChange={(e) => setFormular({ ...formular, address_postal_code: e.target.value })} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="raum-ort" className="text-right text-sm font-medium">Ort *</label>
              <Input id="raum-ort" className="col-span-3" placeholder="Ort" value={formular.address_city} onChange={(e) => setFormular({ ...formular, address_city: e.target.value })} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="raum-weg" className="text-right text-sm font-medium">Wegbeschreibung</label>
              <Input id="raum-weg" className="col-span-3" placeholder="z. B. 2. Stock links" value={formular.directions} onChange={(e) => setFormular({ ...formular, directions: e.target.value })} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="raum-kapazitaet" className="text-right text-sm font-medium">Kapazität</label>
              <Input id="raum-kapazitaet" type="number" className="col-span-3" placeholder="Anzahl Personen" value={formular.kapazitaet} onChange={(e) => setFormular({ ...formular, kapazitaet: parseInt(e.target.value) || 1 })} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="raum-groesse" className="text-right text-sm font-medium">Größe (m²)</label>
              <Input id="raum-groesse" type="number" className="col-span-3" placeholder="z. B. 45" value={formular.size_m2} onChange={(e) => setFormular({ ...formular, size_m2: e.target.value === '' ? '' : parseInt(e.target.value) })} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="raum-kosten" className="text-right text-sm font-medium">Kosten (EUR)</label>
              <Input
                id="raum-kosten"
                type="number"
                step="0.01"
                className="col-span-3"
                placeholder="z. B. 25.00"
                value={formular.cost_cents === '' || formular.cost_cents === null ? '' : (typeof formular.cost_cents === 'number' ? (formular.cost_cents / 100).toFixed(2) : (parseFloat(String(formular.cost_cents)) / 100).toFixed(2))}
                onChange={(e) => {
                  const wert = e.target.value;
                  if (wert === '') {
                    setFormular({ ...formular, cost_cents: '' });
                    return;
                  }
                  const euro = parseFloat(wert);
                  if (!isNaN(euro)) setFormular({ ...formular, cost_cents: Math.round(euro * 100) });
                }}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <span className="text-right text-sm font-medium">Intervall</span>
              <Select value={formular.cost_interval || ''} onValueChange={(v) => setFormular({ ...formular, cost_interval: v })}>
                <SelectTrigger className="col-span-3" aria-label="Intervall">
                  <SelectValue placeholder="Bitte auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hour">pro Stunde</SelectItem>
                  <SelectItem value="day">pro Tag</SelectItem>
                  <SelectItem value="usage">pro Nutzung</SelectItem>
                  <SelectItem value="month">monatlich</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="raum-bild" className="text-right text-sm font-medium">Bild</label>
              <div className="col-span-3 flex items-center gap-2">
                <Input id="raum-bild" className="flex-1" placeholder="Bild-URL" value={formular.image_url} onChange={(e) => setFormular({ ...formular, image_url: e.target.value })} />
                <input
                  type="file"
                  accept="image/*"
                  aria-label="Bild hochladen"
                  onChange={async (e) => {
                    const datei = e.target.files?.[0];
                    if (!datei) return;
                    try {
                      const gespeichert = await uploadRoomImage(datei);
                      setFormular((f) => ({ ...f, image_url: gespeichert.url }));
                      toast.success('Bild hochgeladen');
                    } catch {
                      toast.error('Upload fehlgeschlagen');
                    }
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <span className="text-right text-sm font-medium">Ausstattung</span>
              <div className="col-span-3 flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <label key={tag.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formular.tag_ids.includes(tag.id)}
                      onChange={(e) =>
                        setFormular({
                          ...formular,
                          tag_ids: e.target.checked ? [...formular.tag_ids, tag.id] : formular.tag_ids.filter((id) => id !== tag.id),
                        })
                      }
                    />
                    {tag.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="raum-aktiv" className="text-right text-sm font-medium">Aktiv</label>
              <div className="col-span-3 flex items-center">
                <input id="raum-aktiv" type="checkbox" checked={formular.aktiv} onChange={(e) => setFormular({ ...formular, aktiv: e.target.checked })} className="h-4 w-4" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBearbeitet(null)}>
              Abbrechen
            </Button>
            <Button onClick={() => void speichern()}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={`Raum „${zuLoeschen?.name ?? ''}“ löschen?`}
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={loeschen}
      />
    </Card>
  );
}
